import * as Sentry from '@sentry/react';
import { ssoCodeExchange } from '@constants/endpoints';
import type { AxiosError } from 'axios';
import type { PartnerTokenResult } from './partnerSession';

type SsoTokenRejectionStatus = Exclude<PartnerTokenResult['status'], 'partner'>;

interface SsoTokenRejectionContext {
  hasPartnerMerchantId?: boolean;
  source?: string;
}

export const captureSsoCodeExchangeFailure = (error: unknown): void => {
  const axiosError = error as AxiosError | undefined;
  const wrappedMessage = 'BLR Pulse SSO code exchange failed';
  const safeMessage =
    error instanceof Error && error.message.startsWith('BLR Pulse SSO code')
      ? error.message
      : wrappedMessage;
  const capturedError = new Error(safeMessage);

  Sentry.captureException(capturedError, {
    tags: {
      feature: 'blr_pulse_sso',
      flow: 'code_exchange',
    },
    extra: {
      endpoint: ssoCodeExchange,
      status: axiosError?.response?.status,
      errorCode: axiosError?.code,
      wrappedMessage,
    },
  });
};

export const captureSsoTokenRejection = (
  status: SsoTokenRejectionStatus,
  context: SsoTokenRejectionContext = {},
): void => {
  Sentry.captureMessage('BLR Pulse SSO token rejected', {
    level: 'warning',
    tags: {
      feature: 'blr_pulse_sso',
      flow: 'token_validation',
      rejection_reason: status,
    },
    extra: { ...context },
  });
};

export const addSsoSessionStartedBreadcrumb = (partnerId: string): void => {
  Sentry.addBreadcrumb({
    category: 'blr_pulse_sso',
    message: 'Partner SSO session established',
    level: 'info',
    data: {
      partnerId,
    },
  });
};

export const capturePartnerSsoUnauthorized = (partnerId: string): void => {
  Sentry.captureMessage('BLR Pulse SSO partner session unauthorized', {
    level: 'warning',
    tags: {
      feature: 'blr_pulse_sso',
      flow: 'partner_api_401',
    },
    extra: {
      partnerId,
    },
  });
};

export const capturePartnerSessionErrorPage = (reason: string): void => {
  Sentry.captureMessage('BLR Pulse SSO terminal error displayed', {
    level: 'warning',
    tags: {
      feature: 'blr_pulse_sso',
      flow: 'terminal_error',
      reason,
    },
  });
};

export const capturePartnerCallbackUrlBlocked = (
  reason: 'unsafe_scheme' | 'parse_error',
): void => {
  Sentry.captureMessage('BLR Pulse SSO callback URL blocked', {
    level: 'warning',
    tags: {
      feature: 'blr_pulse_sso',
      flow: 'callback_redirect',
      rejection_reason: reason,
    },
  });
};

export const addPartnerCallbackRedirectFiredBreadcrumb = (): void => {
  Sentry.addBreadcrumb({
    category: 'blr_pulse_sso',
    message: 'Partner callback redirect fired',
    level: 'info',
  });
};
